import { Panel, PanelType } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Shimmer, Text } from "sprint-common-web";

import { GetOrderAuditListRequest } from "../../../../common/api/orders-api/order/GetOrderAuditListRequest";
import { GetOrderAuditListResponse } from "../../../../common/api/orders-api/order/GetOrderAuditListResponse";
import {
    isAssetChangeItem,
    isDeliveryInfoChangeItem,
    isFilenameChangeItem,
    isMaterialSpecificationChangeItem,
    isStatusChangeItem,
    OrderAuditItem
} from "./AuditItem";
import { AssetChangeInfo } from "./components/AssetChangeInfo";
import { DeliveryInfoChangeInfo } from "./components/DeliveryInfoChangeInfo";
import { FilenameChangeInfo } from "./components/FilenameChangeInfo";
import { MaterialSpecificationChangeInfo } from "./components/MaterialSpecificationChangeInfo";
import { renderAuditInfo } from "./components/renderAuditInfo";
import { StatusChangeInfo } from "./components/StatusChangeInfo";

export enum OrderAuditType {
    Cloud = 1,
    OnPrem = 2
}
interface AuditListAsideProps {
    type: OrderAuditType;
    itemId: string;
    isOpen: boolean;
    onClose(): void;
    getOrderAuditList(request: GetOrderAuditListRequest): Promise<GetOrderAuditListResponse>;
}

export const AuditListAside: React.FunctionComponent<AuditListAsideProps> = ({
    type,
    itemId,
    isOpen,
    onClose,
    getOrderAuditList
}: AuditListAsideProps) => {
    const [items, setItems] = useState<OrderAuditItem[]>([]);
    const [hasMore, setHasMore] = useState<boolean>(true);

    useEffect(() => {
        if (isOpen) {
            loadData();
        }
    }, [isOpen]);

    const loadData = () => {
        const request: GetOrderAuditListRequest = {
            orderId: itemId,
            pageSize: 50,
            lastAuditId: getLastAuditId()
        };

        setTimeout(() => {
            if (hasMore) {
                getOrderAuditList(request).then((response: GetOrderAuditListResponse) => {
                    setItems((prevItems) => [...prevItems, ...response.items]);

                    setHasMore(response.hasMore);
                });
            }
        }, 500);
    };

    const getLastAuditId = () => {
        return items.length > 0 ? items[items.length - 1].id : undefined;
    };

    const height = window.innerHeight;

    return (
        <Panel
            isOpen={isOpen}
            type={PanelType.smallFixedFar}
            onDismiss={onClose}
            headerText={"Order changes"}
            closeButtonAriaLabel="Close"
            style={{ height }}
        >
            <InfiniteScroll
                dataLength={items.length}
                hasMore={hasMore}
                next={loadData}
                loader={<Shimmer></Shimmer>}
                height={height - 70}
                endMessage={
                    <Text
                        variant={"medium"}
                        styles={{ root: { textAlign: "center", fontWeight: 600 } }}
                        value={"No previous changes"}
                    />
                }
            >
                {items.map((item) => renderOrderChangeRow(item, type))}
            </InfiniteScroll>
        </Panel>
    );
};

const renderOrderChangeRow = (item: OrderAuditItem, type: OrderAuditType) => {
    const infoChanges: JSX.Element[] = [];

    if (isStatusChangeItem(item)) {
        infoChanges.push(<StatusChangeInfo type={type} item={item} />);
    }

    if (isFilenameChangeItem(item)) {
        infoChanges.push(<FilenameChangeInfo item={item} />);
    }

    if (isDeliveryInfoChangeItem(item)) {
        infoChanges.push(<DeliveryInfoChangeInfo item={item} />);
    }

    if (isMaterialSpecificationChangeItem(item)) {
        infoChanges.push(<MaterialSpecificationChangeInfo item={item} />);
    }

    if (isAssetChangeItem(item)) {
        infoChanges.push(<AssetChangeInfo type={type} item={item} />);
    }

    if (!infoChanges.length) return <></>;

    return (
        <>
            {renderAuditInfo(
                item.updatedBy.includes("\\") ? item.updatedBy.split("\\")[1] : item.updatedBy,
                item.updatedDate
            )}
            {infoChanges}
            <hr />
        </>
    );
};
